import { toolLabels } from '../i18n/toolLabels';
import type { SupportedLanguageCode } from '../i18n/translations';

/** Resolve a localized string from a JSON map; prefers `lang`, then `en`, then first non-empty value. */
export function pickI18nString(
  map: Record<string, string> | null | undefined,
  lang: SupportedLanguageCode,
): string | null {
  if (!map || typeof map !== 'object') return null;
  const direct = map[lang];
  if (direct != null && String(direct).trim() !== '') return String(direct).trim();
  const en = map.en;
  if (en != null && String(en).trim() !== '') return String(en).trim();
  for (const v of Object.values(map)) {
    if (v != null && String(v).trim() !== '') return String(v).trim();
  }
  return null;
}

/**
 * Display name: the family's own name if they renamed the tool → DB i18n map (from the library)
 * → the built-in labels for standard keys → `name`. Never the raw key (2026-10-13: an unknown key
 * like "robot_hoover" used to be shown as-is, because the built-in lookup returns the key itself).
 */
export function getToolDisplayName(tool: any, lang: SupportedLanguageCode): string {
  const name = tool?.name != null ? String(tool.name).trim() : '';
  if (tool?.customized && name) return name;
  const fromJson = pickI18nString(tool?.name_i18n as Record<string, string> | undefined, lang);
  if (fromJson) return fromJson;
  if (tool?.tool_key) {
    const builtIn = toolLabels[lang]?.[tool.tool_key] || toolLabels.en?.[tool.tool_key];
    if (builtIn) return builtIn;
  }
  return name || (tool?.tool_key ? String(tool.tool_key) : '');
}

/** Optional description from DB i18n map or legacy column. */
export function getToolDisplayDescription(tool: any, lang: SupportedLanguageCode): string | null {
  const fromJson = pickI18nString(tool?.description_i18n as Record<string, string> | undefined, lang);
  if (fromJson) return fromJson;
  if (tool?.description != null && String(tool.description).trim() !== '') return String(tool.description);
  return null;
}
